import { useState } from "react";
import { useParams, useNavigate } from "react-router";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useBiometric } from "@/hooks/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { BasicInfoTab } from "./tabs/basic-info-tab";
import { TextsTab } from "./tabs/texts-tab";
import { ReferencesTab } from "./tabs/references-tab";
import { ReferencesTabV2 } from "./tabs/references-tab-v2";

export function BiometricDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: biometric, isLoading } = useBiometric(id!);
  const [refVersion, setRefVersion] = useState<"v1" | "v2">("v2");

  if (isLoading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-admin-text-subtle" /></div>;
  }

  if (!biometric) {
    return (
      <div className="text-center py-12">
        <p className="text-admin-text-subtle mb-4">Biomarkor ikke funnet</p>
        <Button variant="outline" onClick={() => navigate("/biometrics")}>
          Tilbake til listen
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate("/biometrics")}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h2 className="text-xl font-semibold">{biometric.name}</h2>
          {biometric.shortName && (
            <p className="text-sm text-admin-text-subtle">{biometric.shortName}</p>
          )}
        </div>
      </div>

      <Tabs defaultValue="basic">
        <TabsList>
          <TabsTrigger value="basic">Grunninfo</TabsTrigger>
          <TabsTrigger value="texts">Tekster</TabsTrigger>
          <TabsTrigger value="references">Referanser</TabsTrigger>
        </TabsList>

        <TabsContent value="basic">
          <BasicInfoTab biometric={biometric} />
        </TabsContent>

        <TabsContent value="texts">
          <TextsTab biometric={biometric} />
        </TabsContent>

        <TabsContent value="references">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-xs text-admin-text-subtle">Visning:</span>
            <Button
              variant={refVersion === "v2" ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setRefVersion("v2")}
              className="text-xs h-7"
            >
              Grupper
            </Button>
            <Button
              variant={refVersion === "v1" ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setRefVersion("v1")}
              className="text-xs h-7"
            >
              Tabell
            </Button>
          </div>
          {refVersion === "v2" ? (
            <ReferencesTabV2 biometricId={biometric.id} />
          ) : (
            <ReferencesTab biometricId={biometric.id} />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
